import { IExternalAccount, Query } from '@varo/graphql-schema-types';
import { router } from 'expo-router';
import { useCallback } from 'react';
import GetExternalAccounts from 'services/graphql/queries/GetExternalAccountsR2';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import {
  ErrorScreenProps,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useQuery } from 'urql';

export interface GroupedAccountsByInstitution {
  institution: string;
  accounts: IExternalAccount[];
}

export interface UseExternalAccountsResult {
  errorModel: ErrorScreenProps | null;
  loading: boolean;
  response: GroupedAccountsByInstitution[];
  refresh: () => void;
}

const useExternalAccounts = (): UseExternalAccountsResult => {
  const [{ data, fetching: loading, error }, refetchExternalAccounts] =
    useQuery<Query>({
      query: GetExternalAccounts,
      requestPolicy: 'cache-and-network',
    });
  const externalAccounts = data?.myAccounts?.external ?? [];
  const groupedAccountsByInstitution = externalAccounts.reduce(
    (acc: GroupedAccountsByInstitution[], account) => {
      const institutionName = account.institution.name;
      const existingInstitution = acc.find(
        (inst) => inst.institution === institutionName,
      );

      if (existingInstitution) {
        existingInstitution.accounts.push(account);
      } else {
        acc.push({
          institution: institutionName,
          accounts: [account],
        });
      }

      return acc;
    },
    [],
  );

  const refresh = useCallback(() => {
    refetchExternalAccounts({ requestPolicy: 'network-only' });
  }, [refetchExternalAccounts]);

  const cancel = () => {
    router.back();
  };

  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();

  const responseError = useFormattedCombinedError(error);

  const buttons = {
    primaryButton: {
      title: retryButtonTitle,
      onPress: refresh,
    },
    secondaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel = error
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError?.title ?? defaultTitle,
        messageBody: responseError?.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  return {
    errorModel,
    loading,
    response: groupedAccountsByInstitution,
    refresh,
  };
};

export default useExternalAccounts;
